import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Site } from 'app/pages/sites/models';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { combineAll, debounceTime, map, startWith, switchMap } from 'rxjs/operators';
import { IDType } from '../models';
import { FormUIService } from 'app/modules/form-ui-components/form-ui.service';
import { SiteSelectorService } from '../../../../core/services/site.service';
import { SiteOption } from 'app/core/interfaces';

@Component({
    selector: 'dt-site-selector',
    templateUrl: './site-selector.component.html',
})
export class SiteSelectorComponent implements OnInit {

    // 電廠標題
    @Input() label: string = 'common.site';

    // ID Type
    @Input() id_type: IDType = IDType.UserCode;

    // 多選
    @Input() multiple: boolean = false;

    idTypes = IDType;

    // 篩選關鍵字 Subject
    private filterKeywordSubject: BehaviorSubject<string> = new BehaviorSubject('');

    // 電廠清單（包含篩選邏輯的 Observable）
    sites$: Observable<Array<VisiableSite>>;

    // FormControl
    @Input('control') site: UntypedFormControl = new UntypedFormControl('');

    // 隱藏Webtag
    @Input() hideWebtag: boolean = false;

    // 篩選 FormControl
    siteFilter: UntypedFormControl = new UntypedFormControl('');

    // 訂閱者清單 - OnDestroy時解訂用
    private _subscriptions: Array<Subscription> = [];

    labels = new Map<string, string>();

    constructor(
        private uiService: FormUIService,
        private chgDetect: ChangeDetectorRef,
        public siteSelectorService: SiteSelectorService,
    ) { }

    ngOnInit(): void {
        // 篩選器觸發 - 更新篩選關鍵字
        this._subscriptions.push(
            this.siteFilter.valueChanges.pipe(
                debounceTime(100),
                startWith(''),
                map(event => (event?.value || event || '').toLowerCase())
            ).subscribe(keyword => {
                this.filterKeywordSubject.next(keyword);
            })
        );

        // 建立包含篩選邏輯的 sites$ Observable
        this.sites$ = combineLatest([
            this.siteSelectorService.$SiteOptions,
            this.filterKeywordSubject,
            this.siteSelectorService.$IDType
        ]).pipe(
            map(([sites, keyword, idType]) => {
                // 使用 map 優化效能，建立帶有可見性屬性的 site 清單
                return sites.map(site => ({
                    ...site,
                    visiable: this.isSiteVisible(site, keyword, idType)
                } as VisiableSite));
            })
        );

        // 更新 labels Map
        this._subscriptions.push(
            combineLatest([
                this.siteSelectorService.$SiteOptions,
                this.siteSelectorService.$IDType,
            ]).subscribe(([sites, value]) => {
                // 使用 Map 建構函數批量建立，提高效能
                this.labels = new Map(
                    sites.map(s => {
                        let label: string;
                        switch(value) {
                            case IDType.UserCode:
                                label = s.user_site;
                                break;
                            case IDType.OwnerCode:
                                label = s.owner_site;
                                break;
                            case IDType.SystemCode:
                                label = s.site;
                                break;
                        }
                        return [s.site, label];
                    })
                );
            })
        );
    }

    /**
     * 判斷 site 是否應該可見
     * @param site SiteOption 物件
     * @param keyword 篩選關鍵字
     * @param idType ID 類型
     * @returns 是否可見
     */
    private isSiteVisible(site: SiteOption, keyword: string, idType: IDType): boolean {
        if (!keyword) {
            return true; // 沒有關鍵字時全部顯示
        }

        const lowerKeyword = keyword.toLowerCase();

        // 同時檢查 ID 欄位和名字欄位
        let idText: string;
        switch (idType) {
            case IDType.UserCode:
                idText = site.user_site;
                break;
            case IDType.OwnerCode:
                idText = site.owner_site;
                break;
            case IDType.SystemCode:
                idText = site.site;
                break;
            default:
                idText = site.user_site;
        }

        // 檢查 ID 欄位或名字欄位是否包含關鍵字
        return idText.toLowerCase().includes(lowerKeyword) ||
               (site.name && site.name.toLowerCase().includes(lowerKeyword));
    }

    getSelectLabel(): string {
        const sites: Array<VisiableSite> = this.site.value;
        if( Array.isArray(sites) && sites.length ) {
            let site = '';
            switch(this.id_type)
            {
                case IDType.UserCode:
                    site = sites[0].user_site;
                    break;
                case IDType.OwnerCode:
                    site = sites[0].owner_site;
                    break;
                case IDType.SystemCode:
                    site = sites[0].site;
                    break;
            }

            if( sites.length > 1 ) {
                site += `(+${sites.length - 1} Items)`
            }

            return site;
        }

        return '';
    }

}

interface VisiableSite extends SiteOption {
    visiable: boolean;
}
